import { useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, IconButton, LinearProgress, Typography } from "@mui/material";
import { Clapperboard, Crop, Download, ExternalLink, FileText, ImagePlus, Link2, Maximize2, Plus, RefreshCw, Trash2, X } from "lucide-react";

import { AlertNote } from "../../../components/AlertNote";
import { Badge } from "../../../components/Badge";
import { Button } from "../../../components/Button";
import { ImageActionsToolbar } from "../../../components/ImageActionsToolbar";
import { UnifiedModal } from "../../../components/UnifiedModal";
import { brand } from "../../../theme/theme";
import { INLINE_IMAGE_MAX_WIDTH, inlineImageStyle } from "../../../utils/inlineImageStyle";
import { downloadImage } from "../../../utils/urlHelpers";
import { MediaDropZone as SharedMediaDropZone, MediaInput } from "../../../components/MediaDropZone";
import {
  isUploadCancelled,
  publishingErrorText,
  videoUploadErrorText,
} from "../../../services/socialPublishingService.js";
import { ArrowCarousel } from "./ArrowCarousel";
import {
  PlatformIcon,
  VIDEO_MAX_MB,
  adaptationFormat,
  adaptationMeta,
  formatLabel,
  getFormatRule,
  getPlatformMeta,
  imageAspectProblem,
  incompatibleMedia,
  DOCUMENT_MAX_MB,
  isDocumentMedia,
  isPdfFile,
  isVideoExpiredError,
  isVideoMedia,
  mediaAccept,
  mediaWarnings,
  platformFormats,
  platformSupportsDocuments,
  platformSupportsVideo,
  postTextSx,
  sortPickedFiles,
  videoTypesLabel,
} from "./platformMeta";
import { BusyNote, ButtonSpinner } from "./BusyIndicator";
import { DurationBadge, FormatSelector, MediaWarnings, VideoPoster, readVideoMetadata } from "./VideoMedia";
import { DocumentDialog, DocumentFrame, LinkButton } from "./DocumentMedia";
import { ImageCropDialog } from "./ImageCropDialog";
import { hasPostPreview } from "./PostPreviewModal";
import { markdownToHtml } from "../../../utils/markdownClipboard";
import { extractHeadings, markdownToHtmlSimple, markdownToPlain } from "./workflowUtils";

const sectionLabelSx = {
  fontSize: "0.6875rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#6B7280",
};

export const sectionLabelStyle = sectionLabelSx;

export const cardShellSx = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: "16px",
  border: "1px solid #E8ECF2",
  backgroundColor: "#fff",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  overflow: "hidden",
};

// Recommended image size per network, shown in the upload zone.
const IMAGE_SPECS = {
  instagram: { label: "Imágenes del carrusel", size: "1080×1350" },
  facebook: { label: "Imagen del post", size: "1200×630" },
  linkedin: { label: "Imagen del post", size: "1200×627" },
  tiktok: { label: "Fotograma o portada", size: "1080×1920" },
  google_business_profile: { label: "Imagen de la publicación", size: "1200×900" },
  youtube: { label: "Miniatura", size: "1280×720" },
  email_marketing: { label: "Imagen de cabecera", size: "600×300" },
};

const imageSpec = (platform) => IMAGE_SPECS[platform] || { label: "Imagen", size: "1200×630" };

/** Texts of the upload zone and link for what the format admits. */
const uploadCopy = (platform, format) => {
  const rule = getFormatRule(platform, format);
  const spec = imageSpec(platform);
  const videoSpec = `${videoTypesLabel(platform, format)} · hasta ${VIDEO_MAX_MB} MB`;
  const single = rule.maxFiles === 1;
  if (rule.videos && !rule.images) {
    return { title: `Vídeo · ${videoSpec}`, chip: rule.ratio ? "Vídeo · vertical 9:16" : "Vídeo", add: "Añadir vídeo", Icon: Clapperboard };
  }
  if (rule.videos) {
    return {
      title: single ? `Una foto o un vídeo · ${videoSpec}` : `Fotos o vídeo · ${videoSpec}`,
      chip: rule.ratio ? "Vertical 9:16" : `Imagen · ${spec.size}`,
      add: "Añadir foto o vídeo",
      Icon: ImagePlus,
    };
  }
  if (platformSupportsVideo(platform)) {
    // TikTok "Imágenes": a video switches the format to "Vídeo".
    return { title: `Fotos · ${spec.size}, o un vídeo · ${videoSpec}`, chip: `Imagen · ${spec.size}`, add: "Añadir foto o vídeo", Icon: ImagePlus };
  }
  return {
    title: `${spec.label} · ${spec.size}`,
    chip: `Imagen · ${spec.size}`,
    add: "Añadir imagen",
    Icon: ImagePlus,
  };
};

/** Image and PDF icons side by side, for a drop zone that takes both. */
const ImageOrPdfIcon = (props) => (
  <Box sx={{ display: "flex", gap: 1 }}>
    <ImagePlus {...props} />
    <FileText {...props} />
  </Box>
);

/** Drop zone of an adaptation, with the copy and limits of its format; `acceptDocuments` also takes a PDF. */
function MediaDropZone({ platform, format, onFiles, isUploading, acceptDocuments = false }) {
  const rule = getFormatRule(platform, format);
  const copy = uploadCopy(platform, format);
  return (
    <SharedMediaDropZone
      title={acceptDocuments ? `${copy.title}, o un PDF de hasta ${DOCUMENT_MAX_MB} MB` : copy.title}
      chip={copy.chip}
      Icon={acceptDocuments ? ImageOrPdfIcon : copy.Icon}
      accept={[mediaAccept(platform, format), acceptDocuments && "application/pdf,.pdf"].filter(Boolean).join(",")}
      multiple={rule.maxFiles !== 1}
      isUploading={isUploading}
      onFiles={onFiles}
    />
  );
}

/** Small link to add more media (or replace the only file of a single-file format) below the strip. */
function AddMediaLink({ platform, format, onFiles, isUploading, replace = false }) {
  const inputRef = useRef(null);
  const rule = getFormatRule(platform, format);
  const copy = uploadCopy(platform, format);
  const label = replace ? (rule.images ? "Sustituir" : "Sustituir vídeo") : copy.add;
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -0.5 }}>
      <LinkButton
        onClick={() => inputRef.current?.click()}
        busy={isUploading}
        title={replace ? "El archivo actual se quita cuando el nuevo se haya subido" : undefined}
      >
        {isUploading ? (
          <CircularProgress size={13} sx={{ color: brand.pink }} />
        ) : replace ? (
          <RefreshCw size={13} />
        ) : (
          <Plus size={14} />
        )}
        {label}
      </LinkButton>
      <MediaInput
        inputRef={inputRef}
        accept={mediaAccept(platform, format)}
        multiple={!replace && rule.maxFiles !== 1}
        onFiles={onFiles}
      />
    </Box>
  );
}

const UPLOAD_STATUS = {
  queued: "En cola",
  processing: "Procesando…",
};

/** Tile of a video still being uploaded: the local file, its progress and cancel/retry. */
function UploadTile({ upload, compact, onCancel, onRetry, onConnect, onDismiss }) {
  const { phase, progress } = upload;
  const isError = phase === "error";
  return (
    <>
      <Box
        component="video"
        src={upload.url}
        muted
        playsInline
        preload="metadata"
        aria-hidden
        sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", backgroundColor: "#111827" }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          gap: 0.75,
          p: compact ? 1 : 1.5,
          background: isError
            ? "rgba(127, 29, 29, 0.78)"
            : "linear-gradient(180deg, rgba(15,23,42,0) 35%, rgba(15,23,42,0.78) 100%)",
          color: "#fff",
        }}
      >
        {isError ? (
          <>
            <Typography sx={{ fontSize: compact ? "0.6875rem" : "0.8125rem", fontWeight: 600, lineHeight: 1.35 }}>
              {upload.error}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
              {upload.needsConnection && onConnect ? (
                <Box component="button" type="button" onClick={onConnect} disabled={upload.connecting} sx={tileActionSx}>
                  {upload.connecting ? (
                    <CircularProgress size={11} sx={{ color: "inherit" }} />
                  ) : (
                    <Link2 size={12} />
                  )}
                  {upload.connecting ? "Esperando conexión…" : "Conectar y reintentar"}
                </Box>
              ) : (
                <Box component="button" type="button" onClick={onRetry} sx={tileActionSx}>
                  <RefreshCw size={12} /> Reintentar
                </Box>
              )}
              <Box component="button" type="button" onClick={onDismiss} sx={tileActionSx}>
                Quitar
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: compact ? "0.6875rem" : "0.8125rem", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
              {phase === "uploading" ? `Subiendo · ${progress} %` : UPLOAD_STATUS[phase]}
            </Typography>
            <LinearProgress
              variant={phase === "processing" ? "indeterminate" : "determinate"}
              value={phase === "queued" ? 0 : progress}
              aria-label={phase === "processing" ? "Procesando el vídeo" : "Progreso de la subida"}
              sx={{
                height: 5,
                borderRadius: 999,
                backgroundColor: "rgba(255,255,255,0.3)",
                "& .MuiLinearProgress-bar": { background: brand.gradient, borderRadius: 999 },
              }}
            />
          </>
        )}
      </Box>
      {(phase === "uploading" || phase === "queued") && (
        <Box
          component="button"
          type="button"
          aria-label={`Cancelar la subida de ${upload.file.name}`}
          title="Cancelar subida"
          onClick={onCancel}
          sx={{ ...removeButtonSx, opacity: 1 }}
        >
          <X size={14} />
        </Box>
      )}
    </>
  );
}

const tileActionSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  px: 1,
  py: 0.375,
  border: "none",
  borderRadius: "999px",
  backgroundColor: "rgba(255,255,255,0.92)",
  color: "#111827",
  font: "inherit",
  fontSize: "0.75rem",
  fontWeight: 700,
  cursor: "pointer",
  "&:focus-visible": { outline: "2px solid #fff", outlineOffset: 2 },
};

const removeButtonSx = {
  position: "absolute",
  top: 8,
  left: 8,
  width: 26,
  height: 26,
  p: 0,
  border: "none",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(15, 23, 42, 0.72)",
  color: "#fff",
  cursor: "pointer",
  zIndex: 2,
  transition: "opacity 0.15s, background-color 0.15s",
  "&:hover": { backgroundColor: "#DC2626" },
  "&:focus-visible": { opacity: 1, outline: `2px solid ${brand.pink}`, outlineOffset: 2 },
};

const imageActionsSx = {
  position: "absolute",
  top: 8,
  left: 8,
  transition: "opacity 0.15s",
  opacity: { xs: 1, md: 0 },
};

/** Width/height ratio of a media box: the format's (9:16 for Reels and stories), the video's own, or 4:5. */
const mediaRatio = (item, rule, single) => {
  if (rule.ratio) return rule.ratio;
  const width = item?.width || item?.meta?.width;
  const height = item?.height || item?.meta?.height;
  if (single && width && height) return Math.min(Math.max(width / height, 9 / 16), 16 / 9);
  return 4 / 5;
};

const SINGLE_MAX_HEIGHT = 440;
const COMPACT_TILE_HEIGHT = 220;

/**
 * Media of an adaptation: one wide item, or thumbnails three to a row with a
 * counter on each and side arrows to scroll through the rest. Videos play inline
 * before publishing; once published they open the post on the network.
 */
function ImageStrip({
  images,
  uploads = [],
  platform,
  format,
  publishedUrl = "",
  faultyIds = null,
  faultLabel = "",
  onOpenImage,
  onRemoveImage,
  onCropImage = null, // (image) => void — opens the crop dialog
  cropIds = null, // Set of image ids whose shape the format doesn't take
  removingId = null,
  onCancelUpload,
  onRetryUpload,
  onConnectUpload = null,
  onDismissUpload,
  compact = false, // embedded card: fixed-size thumbnails in a wrapping row
  onEditDocumentTitle = null, // LinkedIn PDF
}) {
  const rule = getFormatRule(platform, format);
  const items = [...images, ...uploads.map((upload) => ({ id: upload.key, media_type: "video", _upload: upload }))];
  // Compact: a lone image is a thumbnail too, not the full-width view.
  const single = items.length === 1 && !compact;
  const vertical = Boolean(rule.ratio) || platform === "tiktok";
  const stripRatio = vertical ? "9 / 16" : "4 / 5";
  const Wrapper = single || compact ? Box : ArrowCarousel;
  const wrapperProps = compact
    ? { sx: { display: "flex", flexWrap: "wrap", gap: 1.25 } }
    : single
    ? {}
    : {
      ariaLabel: "Imágenes y vídeos",
      gap: 1,
      itemWidth: "calc((100% - 16px) / 3)",
      arrowOffset: 6,
      arrowSize: 30,
    };
  const networkName = getPlatformMeta(platform).label;

  return (
    <Wrapper {...wrapperProps}>
      {items.map((item, index) => {
        const isVideo = isVideoMedia(item);
        const isDocument = isDocumentMedia(item);
        const upload = item._upload;
        const expired = !upload && Boolean(faultyIds?.has(item.id));
        const needsCrop = !upload && !expired && Boolean(cropIds?.has(item.id));
        const ratio = mediaRatio(upload ? { meta: upload.meta } : item, rule, single);
        const position = `${index + 1} de ${items.length}`;
        const isImage = !isVideo && !isDocument;
        const actions = upload || item.id == null || removingId != null ? [] : [
          isImage && onOpenImage && { label: "Ampliar", Icon: Maximize2, onClick: () => onOpenImage(item.image) },
          isImage && onCropImage && item.width && { label: "Recortar", Icon: Crop, onClick: () => onCropImage(item) },
          isImage && { label: "Descargar", Icon: Download, onClick: () => downloadImage(item.image) },
          onRemoveImage && {
            label: isVideo ? "Eliminar vídeo" : isDocument ? "Eliminar documento" : "Eliminar imagen",
            Icon: Trash2,
            onClick: () => onRemoveImage(item),
          },
        ].filter(Boolean);
        // Single image keeps its own shape; videos get a fixed box so nothing jumps while they load.
        const frameSx = compact && !isVideo && !upload
          ? { height: COMPACT_TILE_HEIGHT, width: "auto", display: "flex" }
          : single
          ? isVideo
            ? { aspectRatio: String(ratio), maxHeight: SINGLE_MAX_HEIGHT, maxWidth: SINGLE_MAX_HEIGHT * ratio, mx: "auto" }
            : { aspectRatio: "auto" }
          : { aspectRatio: stripRatio };
        const frameBaseSx = {
          position: "relative",
          p: 0,
          border: "none",
          borderRadius: "12px",
          overflow: "hidden",
          backgroundColor: isVideo ? "#111827" : single ? "#F6F7F9" : "#F3F4F6",
          display: "block",
          width: "100%",
          boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(15, 23, 42, 0.08)",
          ...(expired || needsCrop ? { outline: "2px solid #DC2626", outlineOffset: -2 } : {}),
          ...frameSx,
        };

        let frame;
        if (upload) {
          frame = (
            <Box sx={frameBaseSx}>
              <UploadTile
                upload={upload}
                compact={!single}
                onCancel={() => onCancelUpload?.(upload)}
                onRetry={() => onRetryUpload?.(upload)}
                onConnect={onConnectUpload ? () => onConnectUpload(upload) : null}
                onDismiss={() => onDismissUpload?.(upload)}
              />
            </Box>
          );
        } else if (isVideo && publishedUrl) {
          frame = (
            <Box
              component="a"
              href={publishedUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
              aria-label={`Ver el vídeo publicado en ${networkName} (se abre en otra pestaña)`}
              title={`Ver en ${networkName}`}
              sx={{
                ...frameBaseSx,
                cursor: "pointer",
                "&:focus-visible": { outline: `2px solid ${brand.pink}`, outlineOffset: 2 },
              }}
            >
              <VideoPoster src={item.image} playSize={single ? 52 : 36} />
              <DurationBadge seconds={item.duration} />
              <Box
                component="span"
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 0.75,
                  py: "1px",
                  borderRadius: "6px",
                  backgroundColor: "rgba(255,255,255,0.92)",
                  color: "#111827",
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                }}
              >
                <ExternalLink size={11} /> {single ? `Ver en ${networkName}` : ""}
              </Box>
            </Box>
          );
        } else if (isDocument) {
          frame = (
            <DocumentFrame
              item={item}
              onEditTitle={onEditDocumentTitle}
              sx={expired ? { outline: "2px solid #DC2626", outlineOffset: -2 } : {}}
            />
          );
        } else if (isVideo) {
          frame = (
            <Box sx={frameBaseSx}>
              {item.video_url ? (
                <Box
                  component="video"
                  src={item.video_url}
                  poster={item.image || undefined}
                  controls
                  playsInline
                  preload="metadata"
                  aria-label={`Vídeo ${position}`}
                  onClick={(event) => event.stopPropagation()}
                  sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                />
              ) : (
                <VideoPoster src={item.image} showPlay={false} />
              )}
              {!item.video_url && <DurationBadge seconds={item.duration} />}
            </Box>
          );
        } else {
          frame = (
            <Box
              component="button"
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onOpenImage?.(item.image);
              }}
              aria-label={`Ver imagen ${position}`}
              sx={{
                ...frameBaseSx,
                cursor: onOpenImage ? "zoom-in" : "default",
                "& img": { transition: "transform 0.25s ease" },
                "&:hover img": { transform: "scale(1.04)" },
                "&:focus-visible": { outline: `2px solid ${brand.pink}`, outlineOffset: 2 },
              }}
            >
              <Box
                component="img"
                src={item.image}
                alt=""
                loading="lazy"
                sx={
                  compact
                    ? { height: "100%", width: "auto", maxWidth: COMPACT_TILE_HEIGHT * 2, objectFit: "contain", display: "block" }
                    : single
                    ? { width: "100%", height: "auto", maxHeight: SINGLE_MAX_HEIGHT, objectFit: "contain", display: "block", mx: "auto" }
                    : { width: "100%", height: "100%", objectFit: "cover", display: "block" }
                }
              />
            </Box>
          );
        }

        return (
          <Box
            key={item.id ?? item.image}
            sx={{
              position: "relative",
              // Images take their own width (whole image); videos and uploads a fixed box.
              ...(compact
                ? {
                    flexShrink: 0,
                    ...(isDocument
                      ? { width: 340, maxWidth: "100%" }
                      : isVideo || upload
                      ? { width: Math.round(COMPACT_TILE_HEIGHT * (vertical ? 9 / 16 : 4 / 5)) }
                      : {}),
                  }
                : {}),
              "&:hover .mk-image-actions, &:focus-within .mk-image-actions": { opacity: 1 },
            }}
          >
            <Box sx={{ position: "relative", ...(single && isVideo ? { maxWidth: frameSx.maxWidth, mx: "auto" } : {}) }}>
              {frame}
              {items.length > 1 && !upload && !(isVideo && publishedUrl) && !expired && !needsCrop && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    px: 0.75,
                    py: "1px",
                    borderRadius: "6px",
                    backgroundColor: "rgba(255,255,255,0.92)",
                    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.12)",
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    color: "#111827",
                    pointerEvents: "none",
                  }}
                >
                  {index + 1}/{items.length}
                </Box>
              )}
              {needsCrop && (
                <Box
                  component={onCropImage ? "button" : "span"}
                  type={onCropImage ? "button" : undefined}
                  onClick={onCropImage ? (event) => {
                    event.stopPropagation();
                    onCropImage(item);
                  } : undefined}
                  title="La red no admite esta proporción"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 0.75,
                    py: "1px",
                    border: "none",
                    borderRadius: "6px",
                    backgroundColor: "#DC2626",
                    color: "#fff",
                    font: "inherit",
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    cursor: onCropImage ? "pointer" : "default",
                    zIndex: 2,
                  }}
                >
                  <Crop size={11} /> Recortar
                </Box>
              )}
              {expired && (
                <Box
                  component="span"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    px: 0.75,
                    py: "1px",
                    borderRadius: "6px",
                    backgroundColor: "#DC2626",
                    color: "#fff",
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                  }}
                >
                  {faultLabel}
                </Box>
              )}
              {removingId === item.id && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 3,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    borderRadius: "12px",
                    backgroundColor: "rgba(255,255,255,0.78)",
                  }}
                >
                  <CircularProgress size={22} sx={{ color: brand.pink }} />
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#111827" }}>Eliminando…</Typography>
                </Box>
              )}
              {actions.length > 0 && (
                <ImageActionsToolbar className="mk-image-actions" actions={actions} sx={imageActionsSx} />
              )}
            </Box>
          </Box>
        );
      })}
    </Wrapper>
  );
}

const blogArticleSx = {
  fontSize: "0.875rem",
  lineHeight: 1.65,
  color: "#374151",
  wordBreak: "break-word",
  "& > :first-of-type": { mt: 0 },
  "& h1, & h2, & h3, & h4": { color: "#111827", lineHeight: 1.3, mt: 2, mb: 0.75 },
  "& h1": { fontSize: "1.125rem" },
  "& h2": { fontSize: "1rem" },
  "& h3, & h4": { fontSize: "0.9375rem" },
  "& p": { my: 0.75 },
  "& ul, & ol": { my: 0.75, pl: 2.5 },
  "& a": { color: brand.pink },
  "& img": { ...inlineImageStyle(INLINE_IMAGE_MAX_WIDTH), my: 1.5 },
};

function BlogBody({ title, text, onReadMore, expanded = false }) {
  if (expanded) {
    return (
      <Box sx={blogArticleSx} dangerouslySetInnerHTML={{ __html: markdownToHtml(text || "") }} />
    );
  }
  const headings = extractHeadings(text);
  const intro = markdownToPlain(
    String(text || "")
      .split("\n")
      .filter((line) => !/^#{1,6}\s/.test(line))
      .join("\n")
  ).slice(0, 360);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, minHeight: 0 }}>
      <Typography sx={{ fontWeight: 700, fontSize: "1rem", lineHeight: 1.3, color: "#111827" }}>
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.875rem",
          color: "#4B5563",
          lineHeight: 1.55,
          display: "-webkit-box",
          WebkitLineClamp: 4,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {intro}
      </Typography>
      {headings.length > 0 && (
        <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none", borderTop: "1px solid #F1F5F9", pt: 1.25 }}>
          {headings.map((heading) => (
            <Box
              component="li"
              key={heading}
              sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#374151", lineHeight: 1.8 }}
            >
              · {heading}
            </Box>
          ))}
        </Box>
      )}
      {onReadMore && (
        <Box
          component="button"
          type="button"
          onClick={onReadMore}
          sx={{
            alignSelf: "flex-end",
            border: "none",
            background: "none",
            p: 0,
            mt: "auto",
            fontWeight: 700,
            fontSize: "0.8125rem",
            color: brand.pink,
            cursor: "pointer",
          }}
        >
          Leer completo
        </Box>
      )}
    </Box>
  );
}

const isActiveUpload = (upload) => upload.phase !== "error";

/**
 * Preview of a piece as the client will see it: network header, images and
 * text. With several adaptations it shows a switcher between networks.
 */
export function ContentPreviewCard({
  title,
  adaptations,
  initialAdaptationId = null,
  status = null, // { label, color }
  actions = null,
  footerNote = null,
  onOpenImage,
  onReadMore,
  onAdaptationChange,
  highlighted = false,
  selected: isSelected = false,
  onSelect,
  bodyOverride = null,
  textEditor = null, // replaces the framed text box: the editor is already the frame
  onAddImages = null, // (adaptation, files) => Promise (null = failed) — shows the upload zone
  onRemoveImage = null, // (adaptation, image) => Promise — shows a remove button on each image
  onAddVideo = null, // (adaptation, file, { onProgress, signal, meta }) => Promise<item>, throws on error
  onAddDocument = null, // (adaptation, file, title) => Promise, throws on error — a PDF dropped on a LinkedIn card
  onChangeDocumentTitle = null, // (adaptation, title) => Promise, throws on error
  onCropImage = null, // (adaptation, image, box) => Promise, throws on error
  onChangeFormat = null, // (adaptation, format) => Promise, throws on error — shows the format selector
  initialVideos = null, // File[] picked before the piece existed: uploaded once, on mount
  onUploadingChange = null, // (boolean) => void — a video is queued, uploading or processing
  mediaError = null, // { message, mediaId }: last publishing error about a file (e.g. video expired in bundle.social)
  onConnectNetwork = null, // (adaptation) => Promise<boolean>: opens the connect portal, true once connected
  hideTitleInHeader = false, // the page already shows the piece name
  textLines = 7,
  headerAction = null, // after the status badge (e.g. remove the network)
  embedded = false, // inside NetworkTabsPanel: no frame or header; format, hint and status on one row
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [removingImageId, setRemovingImageId] = useState(null);
  const [documentDialog, setDocumentDialog] = useState(null); // "add" | "title"
  const [cropImage, setCropImage] = useState(null);
  const [documentFile, setDocumentFile] = useState(null); // PDF dropped on the card, preselected in the dialog
  const [selectedId, setSelectedId] = useState(initialAdaptationId);
  // Videos being uploaded: { key, adaptation, file, url, meta, phase, progress, error, controller, replaces }.
  const [uploads, setUploads] = useState([]);
  const uploadsRef = useRef([]);
  const runningRef = useRef(false);
  const formatRef = useRef("post"); // the format chosen now, for uploads queued in an older render
  const [rejected, setRejected] = useState([]);
  const [pendingFormat, setPendingFormat] = useState(null);
  const [resolvedError, setResolvedError] = useState(""); // a publishing error already fixed by replacing its file
  // Videos dropped on a format without video (TikTok "Imágenes"): { format, label, files } until the user decides.
  const [videosForFormat, setVideosForFormat] = useState(null);
  // Format whose rules the current media breaks: { format, label, items } until the user decides.
  const [formatSwitch, setFormatSwitch] = useState(null);
  // Already scheduled in bundle.social: { format, label, items } until the user confirms.
  const [formatConfirm, setFormatConfirm] = useState(null);
  const reuploadRef = useRef(null);
  const [formatError, setFormatError] = useState("");
  const [formatNotice, setFormatNotice] = useState("");
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const list = useMemo(() => adaptations || [], [adaptations]);
  const selected = useMemo(
    () => list.find((a) => a.id === selectedId) || list[0] || null,
    [list, selectedId]
  );

  useEffect(() => {
    if (initialAdaptationId) setSelectedId(initialAdaptationId);
  }, [initialAdaptationId]);

  // The parent blocks leaving the step: unmounting the card would lose the upload.
  const isUploadingVideo = uploads.some((upload) => ["queued", "uploading", "processing"].includes(upload.phase));
  useEffect(() => {
    onUploadingChange?.(isUploadingVideo);
  }, [isUploadingVideo]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onUploadingChange?.(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateUploads = (updater) => {
    uploadsRef.current = updater(uploadsRef.current);
    setUploads(uploadsRef.current);
  };
  const patchUpload = (key, changes) =>
    updateUploads((items) => items.map((item) => (item.key === key ? { ...item, ...changes } : item)));
  const dropUpload = (key) =>
    updateUploads((items) => {
      const gone = items.find((item) => item.key === key);
      if (gone) URL.revokeObjectURL(gone.url);
      return items.filter((item) => item.key !== key);
    });

  // Free the local previews when the card goes away.
  useEffect(() => () => uploadsRef.current.forEach((item) => URL.revokeObjectURL(item.url)), []);

  // A video upload takes a while: closing the tab would lose it.
  const hasActiveUploads = uploads.some(isActiveUpload);
  useEffect(() => {
    if (!hasActiveUploads) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasActiveUploads]);

  // One at a time: a scheduled piece re-uploads its images to bundle.social on each removal.
  const handleRemoveImage = async (image) => {
    if (!onRemoveImage || !selected || removingImageId != null) return;
    setRemovingImageId(image.id);
    try {
      await onRemoveImage(selected, image);
    } finally {
      setRemovingImageId(null);
    }
  };

  // Replacing the only file of a single-file format: the old one goes once the new one is in.
  const removeReplaced = async (adaptation, replaced) => {
    for (const item of replaced || []) {
      if (item.id == null) continue;
      setRemovingImageId(item.id);
      try {
        await onRemoveImage?.(adaptation, item);
      } finally {
        setRemovingImageId(null);
      }
    }
  };

  const handleAddImages = async (files, replaces = []) => {
    if (!onAddImages || !selected) return null;
    setIsUploading(true);
    try {
      const result = await onAddImages(selected, files);
      if (result !== null && replaces.length) await removeReplaced(selected, replaces);
      return result;
    } finally {
      setIsUploading(false);
    }
  };

  // Videos go one by one: each request keeps a backend worker busy while it forwards the file.
  const uploadOne = async (upload) => {
    // A video the format can't take is never sent: the network would reject it with a generic error.
    const meta = upload.meta || (await readVideoMetadata(upload.url));
    const [problem] = mediaWarnings(upload.adaptation?.platform, upload.format, [
      { media_type: "video", ...(meta || {}), file_size: upload.file.size },
    ]);
    if (problem) {
      patchUpload(upload.key, { phase: "error", error: problem, meta, needsConnection: false, controller: null });
      return;
    }
    const controller = new AbortController();
    patchUpload(upload.key, { phase: "uploading", progress: 0, error: "", needsConnection: false, controller });
    try {
      await onAddVideo(upload.adaptation, upload.file, {
        signal: controller.signal,
        meta: upload.meta,
        onProgress: (fraction) =>
          patchUpload(
            upload.key,
            fraction >= 1 ? { phase: "processing", progress: 100 } : { progress: Math.round(fraction * 100) }
          ),
      });
      dropUpload(upload.key);
      if (upload.replaces?.length) await removeReplaced(upload.adaptation, upload.replaces);
      upload.onDone?.();
    } catch (err) {
      if (isUploadCancelled(err)) dropUpload(upload.key);
      else
        patchUpload(upload.key, {
          phase: "error",
          error: videoUploadErrorText(err),
          needsConnection: err?.response?.data?.code === "not_connected",
          controller: null,
        });
    }
  };

  const runUploads = async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      let next = uploadsRef.current.find((item) => item.phase === "queued");
      while (next) {
        await uploadOne(next);
        next = uploadsRef.current.find((item) => item.phase === "queued");
      }
    } finally {
      runningRef.current = false;
    }
  };

  const queueVideos = (files, replaces = [], onDone = null, format = formatRef.current) => {
    if (!onAddVideo || !selected || !files.length) return;
    const added = files.map((file) => ({
      key: `upload-${Date.now()}-${Math.random()}`,
      adaptation: selected,
      format,
      file,
      url: URL.createObjectURL(file),
      meta: null,
      phase: "queued",
      progress: 0,
      error: "",
      controller: null,
      replaces,
      onDone,
    }));
    updateUploads((items) => [...items, ...added]);
    // Duration and size from the local file, so warnings show while it uploads.
    added.forEach((upload) =>
      readVideoMetadata(upload.url).then((meta) => {
        if (uploadsRef.current.some((item) => item.key === upload.key)) patchUpload(upload.key, { meta });
      })
    );
    runUploads();
  };

  const initialVideosQueuedRef = useRef(false);
  useEffect(() => {
    if (initialVideosQueuedRef.current || !initialVideos?.length || !onAddVideo || !selected) return;
    initialVideosQueuedRef.current = true;
    queueVideos(initialVideos);
  }, [initialVideos, onAddVideo, selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancelUpload = (upload) => {
    if (upload.controller) upload.controller.abort();
    else dropUpload(upload.key);
  };

  const handleRetryUpload = (upload) => {
    // The format may have changed since it failed.
    patchUpload(upload.key, { phase: "queued", progress: 0, error: "", connecting: false, format: formatRef.current });
    runUploads();
  };

  // Red sin conectar: se conecta en otra pestaña y, al volver ya conectada, se reintenta.
  const handleConnectUpload = async (upload) => {
    patchUpload(upload.key, { connecting: true });
    const connected = await onConnectNetwork(upload.adaptation);
    if (!uploadsRef.current.some((item) => item.key === upload.key)) return;
    if (connected) handleRetryUpload(upload);
    else patchUpload(upload.key, { connecting: false });
  };

  const handleChangeFormat = async (format) => {
    if (!onChangeFormat || !selected || pendingFormat) return false;
    setPendingFormat(format);
    setFormatError("");
    setFormatNotice("");
    try {
      await onChangeFormat(selected, format);
      return true;
    } catch (err) {
      // The selection goes back to the saved format.
      setFormatError(publishingErrorText(err, "No se ha podido cambiar el formato."));
      return false;
    } finally {
      setPendingFormat(null);
    }
  };

  // Si el formato nuevo no admite lo subido, se avisa antes: quitarlo y cambiar, o cancelar.
  const requestFormatChange = (format) => {
    if (format === adaptationFormat(selected)) return;
    const items = incompatibleMedia(selected?.platform, format, selected?.images);
    if (selected?.social_post?.status === "scheduled") {
      setFormatError("");
      setFormatConfirm({ format, label: formatLabel(selected.platform, format), items: onRemoveImage ? items : [] });
      return;
    }
    if (!items.length || !onRemoveImage) {
      setFormatSwitch(null);
      handleChangeFormat(format);
      return;
    }
    setFormatSwitch({ format, label: formatLabel(selected.platform, format), items });
  };

  const confirmFormatSwitch = async () => {
    const { format, items } = formatSwitch;
    setFormatSwitch(null);
    setPendingFormat(format);
    try {
      await removeReplaced(selected, items);
    } catch {
      setPendingFormat(null);
      return;
    }
    setPendingFormat(null);
    await handleChangeFormat(format);
  };

  // The modal stays open until bundle.social answers; on error it shows why.
  const confirmScheduledFormat = async () => {
    const { format, items } = formatConfirm;
    if (items.length) {
      setPendingFormat(format);
      try {
        await removeReplaced(selected, items);
      } catch {
        setPendingFormat(null);
        setFormatError("No se han podido quitar los archivos que no admite el formato.");
        return;
      }
      setPendingFormat(null);
    }
    if (await handleChangeFormat(format)) setFormatConfirm(null);
  };

  // Switch to the format that takes video, then upload what was waiting for it.
  const switchFormatAndUpload = async (pending, { automatic = false } = {}) => {
    setVideosForFormat(null);
    const ok = await handleChangeFormat(pending.format);
    if (!ok) return;
    if (automatic) setFormatNotice(`Formato cambiado a «${pending.label}» para poder subir el vídeo.`);
    queueVideos(pending.files, [], null, pending.format);
  };

  const handleSelect = (adaptation) => {
    setSelectedId(adaptation.id);
    onAdaptationChange?.(adaptation);
  };

  const platform = selected?.platform;
  const meta = getPlatformMeta(platform);
  const images = selected?.images || [];
  const isBlog = platform === "blog";
  const format = adaptationFormat(selected);
  formatRef.current = format;
  const rule = getFormatRule(platform, format);
  const formatOptions = platformFormats(platform);
  const currentFormatOption = formatOptions.find((option) => option.value === format);
  // Embedded: always a "Formato" row; one tab when the network has only Post or it can't be changed.
  const canPickFormat = formatOptions.length > 1 && Boolean(onChangeFormat) && !isPublished;
  const shownFormatOption = currentFormatOption || { value: format, label: formatLabel(platform, format), hint: "" };
  const isPublished = selected?.social_post?.status === "published";
  const publishedUrl = isPublished ? selected?.social_post?.permalink || "" : "";
  const canUploadVideo = Boolean(onAddVideo);
  const cardUploads = uploads.filter((upload) => upload.adaptation?.id === selected?.id);
  const activeUpload = cardUploads.find((upload) => upload.phase === "uploading" || upload.phase === "processing");
  const isEditingMedia = Boolean(onAddImages || onAddVideo || onChangeFormat);
  // Blog images go inside the text (the editor takes them), not below it.
  const canAddMedia = !isBlog && Boolean(onAddImages || canUploadVideo);
  // Single-file formats replace their file instead of adding a second one.
  const replacing = rule.maxFiles === 1 && images.length > 0;

  const handleFiles = (fileList) => {
    const pdf = canAttachDocument && Array.from(fileList || []).find(isPdfFile);
    if (pdf) {
      // The post carries the document alone: the rest of the files are left out.
      setRejected(
        Array.from(fileList)
          .filter((file) => file !== pdf)
          .map((file) => ({ name: file.name, reason: "Un post lleva un documento o imágenes, no ambos." }))
      );
      setDocumentFile(pdf);
      setDocumentDialog("add");
      return;
    }
    const replaces = replacing ? images : [];
    const sorted = sortPickedFiles(platform, format, fileList, replacing ? [] : images);
    const pendingForFormat = sorted.videosForFormat;
    if (!canUploadVideo || (pendingForFormat && !onChangeFormat)) {
      // A parent without video upload, or a format the user can't change from here.
      const reason = canUploadVideo
        ? `${currentFormatOption?.label || "Este formato"} no admite vídeo.`
        : "Aquí solo se pueden añadir imágenes.";
      const blocked = [...(canUploadVideo ? [] : sorted.videos), ...(pendingForFormat?.files || [])];
      sorted.rejected.push(...blocked.map((file) => ({ name: file.name, reason })));
      if (!canUploadVideo) sorted.videos = [];
    }
    setRejected(sorted.rejected);
    setVideosForFormat(null);
    if (sorted.images.length) handleAddImages(sorted.images, replaces);
    if (sorted.videos.length) queueVideos(sorted.videos, replaces);
    if (canUploadVideo && onChangeFormat && pendingForFormat) {
      // Nothing to lose with an empty card: switch straight away. Otherwise, ask.
      if (!images.length && !sorted.images.length) switchFormatAndUpload(pendingForFormat, { automatic: true });
      else setVideosForFormat(pendingForFormat);
    }
  };

  // Warnings against the chosen format, counting the videos still uploading.
  const pendingVideos = cardUploads
    .filter((upload) => upload.phase !== "error")
    .map((upload) => ({ media_type: "video", ...(upload.meta || {}), file_size: upload.file.size }));
  const warnings =
    !isEditingMedia || isPublished || isBlog
      ? []
      : mediaWarnings(platform, format, [...(replacing && pendingVideos.length ? [] : images), ...pendingVideos]);

  // A file at fault: the error of the last scheduling attempt (`media_id`) or of the post (expired video).
  const postError = selected?.social_post?.status === "error" ? selected.social_post.error_message : "";
  const errorInfo =
    mediaError || (isVideoExpiredError(postError) ? { message: postError, mediaId: null } : null);
  const isExpired = isVideoExpiredError(errorInfo?.message);
  const faultyIds =
    errorInfo && errorInfo.message !== resolvedError
      ? new Set(
        errorInfo.mediaId != null
          ? [errorInfo.mediaId]
          : isExpired
            ? images.filter(isVideoMedia).map((item) => item.id)
            : []
      )
      : null;
  const faultyItems = faultyIds ? images.filter((item) => faultyIds.has(item.id)) : [];
  const cropIds = !isEditingMedia || isPublished || isBlog
    ? null
    : new Set(images.filter((item) => imageAspectProblem(platform, format, item)).map((item) => item.id));
  // A faulty PDF is removed and attached again from its own dialog, not replaced here.
  const canReplaceFaulty =
    faultyItems.length > 0 &&
    !isPublished &&
    !faultyItems.some(isDocumentMedia) &&
    (faultyItems.some(isVideoMedia) ? canUploadVideo : Boolean(onAddImages));
  const hasMedia = images.length > 0 || cardUploads.length > 0;
  const hasDocument = images.some(isDocumentMedia);
  const canAttachDocument =
    Boolean(onAddDocument) && platformSupportsDocuments(platform) && !hasMedia && !isPublished;
  const submitDocument = (file, title) =>
    documentDialog === "add" ? onAddDocument(selected, file, title) : onChangeDocumentTitle(selected, title);

  const handleReplaceFaulty = (fileList) => {
    const sorted = sortPickedFiles(platform, format, Array.from(fileList || []).slice(0, faultyItems.length), images.filter((item) => !faultyIds.has(item.id)));
    setRejected(sorted.rejected);
    const replaces = faultyItems;
    const resolve = () => setResolvedError(errorInfo?.message || "");
    if (sorted.videos.length) {
      queueVideos(sorted.videos, replaces, resolve);
    } else if (sorted.images.length) {
      handleAddImages(sorted.images, replaces).then((result) => result !== null && resolve());
    }
  };

  // Inside the embedded frame the inner boxes lose their own border.
  const textBlock = rule.noText ? (
      // Stories carry no text in the network: nothing to write or show.
      <Box
        role="note"
        sx={{
          minHeight: 120,
          p: 2,
          borderRadius: embedded ? 0 : "12px",
          border: embedded ? "none" : "1px dashed #E5E7EB",
          backgroundColor: "#F9FAFB",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          textAlign: "center",
        }}
      >
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, color: "#374151" }}>
          Las historias no llevan texto
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", color: "#6B7280", lineHeight: 1.45, maxWidth: 320 }}>
          {meta.label} solo publica la imagen o el vídeo. Si quieres texto, inclúyelo dentro del archivo.
        </Typography>
      </Box>
    ) : textEditor ? (
      // Writing in a card selects it, like clicking its header.
      <Box
        onFocusCapture={isSelected ? undefined : onSelect}
        sx={{ display: "flex", flexDirection: "column" }}
      >
        {textEditor}
      </Box>
    ) : (
    <Box
      sx={{
        p: 1.75,
        borderRadius: embedded ? 0 : "12px",
        border: embedded ? "none" : "1px solid #F1F5F9",
        backgroundColor: "#FBFCFD",
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {bodyOverride ? (
        bodyOverride
      ) : isBlog ? (
        <BlogBody title={title} text={selected?.text} onReadMore={onReadMore} expanded={!textLines} />
      ) : (
        <>
          <Box
            sx={{
              fontSize: "0.875rem",
              lineHeight: 1.6,
              color: "#374151",
              wordBreak: "break-word",
              ...(textLines
                ? {
                    display: "-webkit-box",
                    WebkitLineClamp: textLines,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }
                : {}),
              ...postTextSx(platform),
            }}
            dangerouslySetInnerHTML={{ __html: markdownToHtmlSimple(selected?.text || "") }}
          />
          {onReadMore && textLines && (selected?.text || "").length > 280 && (
            <Box
              component="button"
              type="button"
              onClick={onReadMore}
              sx={{
                alignSelf: "flex-end",
                mt: "auto",
                border: "none",
                background: "none",
                p: 0,
                fontWeight: 700,
                fontSize: "0.8125rem",
                color: brand.pink,
                cursor: "pointer",
              }}
            >
              Leer completo
            </Box>
          )}
        </>
      )}
    </Box>
    );
  const queuedCount = cardUploads.filter((upload) => upload.phase === "queued").length;
  const uploadNotice = activeUpload ? (
    <AlertNote variant="danger" sx={{ p: 1.5 }}>
      <Box component="span" role="status" aria-live="polite">
        <Box component="span" sx={{ display: "block", fontWeight: 700, mb: 0.25 }}>
          No cierres esta página hasta que termine.
        </Box>
        {activeUpload.phase === "uploading"
          ? `Subiendo «${activeUpload.file.name}» · ${activeUpload.progress} %.`
          : `Procesando «${activeUpload.file.name}» puede tardar un par de minutos.`}
        {queuedCount > 0 && ` ${queuedCount} más en cola.`}
      </Box>
    </AlertNote>
  ) : null;
  const canDropOnStrip = embedded && hasMedia && !isPublished && canAddMedia;
  const imageStrip = (
        <ImageStrip
          compact={embedded}
          images={images}
          uploads={cardUploads}
          platform={platform}
          format={format}
          publishedUrl={publishedUrl}
          faultyIds={faultyIds}
          faultLabel={isExpired ? "Caducado" : "Revisar"}
          onOpenImage={onOpenImage}
          onRemoveImage={onRemoveImage && selected ? handleRemoveImage : null}
          onCropImage={onCropImage && selected ? setCropImage : null}
          cropIds={cropIds}
          removingId={removingImageId}
          onCancelUpload={handleCancelUpload}
          onRetryUpload={handleRetryUpload}
          onConnectUpload={onConnectNetwork ? handleConnectUpload : null}
          onDismissUpload={(upload) => dropUpload(upload.key)}
          onEditDocumentTitle={onChangeDocumentTitle && !isPublished ? () => setDocumentDialog("title") : null}
        />
  );
  const mediaBlock = hasMedia ? (
      <>
        {embedded && uploadNotice ? (
          // El aviso al lado de las miniaturas si cabe; si no, debajo.
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 2 }}>
            <Box sx={{ flex: "0 1 auto", minWidth: 0 }}>{imageStrip}</Box>
            <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>{uploadNotice}</Box>
          </Box>
        ) : (
          imageStrip
        )}
        {canAddMedia && !hasDocument && (
          <AddMediaLink
            platform={platform}
            format={format}
            onFiles={handleFiles}
            isUploading={isUploading}
            replace={replacing}
          />
        )}
      </>
    ) : canAddMedia ? (
      <>
        <MediaDropZone
          platform={platform}
          format={format}
          onFiles={handleFiles}
          isUploading={isUploading}
          acceptDocuments={canAttachDocument}
        />
      </>
    ) : null;

  return (
    <Box
      sx={
        embedded
          ? { width: "100%", display: "flex", flexDirection: "column", gap: 2 }
          : {
              ...cardShellSx,
              height: "100%",
              transition: "border-color 0.15s, box-shadow 0.15s",
              ...(highlighted ? { borderColor: brand.softBorder, boxShadow: `0 0 0 3px ${brand.soft}` } : {}),
              ...(isSelected ? { borderColor: brand.pink, boxShadow: `0 0 0 3px ${brand.soft}` } : {}),
            }
      }
    >
      {embedded && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", minHeight: 38 }}>
            <Typography component="span" sx={sectionLabelSx}>
              Formato
            </Typography>
            <FormatSelector
              options={canPickFormat ? formatOptions : [shownFormatOption]}
              value={format}
              onChange={canPickFormat ? requestFormatChange : () => {}}
              disabled={Boolean(pendingFormat) || Boolean(activeUpload)}
              busyValue={pendingFormat}
              networkLabel={meta.label}
            />
            {currentFormatOption?.hint && (
              <Typography component="span" sx={{ fontSize: "0.8125rem", color: "#6B7280" }}>
                {currentFormatOption.hint}
              </Typography>
            )}
            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
              {/* Networks without phone preview (blog, YouTube, email) get no preview column */}
              {!hasPostPreview(platform) && (
                <Typography component="span" sx={{ fontSize: "0.8125rem", color: "#6B7280", mr: 0.5 }}>
                  Sin vista previa en móvil
                </Typography>
              )}
              {status && <Badge color={status.color}>{status.label}</Badge>}
              {headerAction}
            </Box>
          </Box>
          {formatError && (
            <Typography role="alert" sx={{ fontSize: "0.75rem", color: "#B91C1C", fontWeight: 600 }}>
              {formatError}
            </Typography>
          )}
          {formatNotice && (
            <Typography role="status" sx={{ fontSize: "0.75rem", color: "#066CC0", fontWeight: 600 }}>
              {formatNotice}
            </Typography>
          )}
        </Box>
      )}

      {/* Header */}
      {!embedded && (
      <Box
        role={onSelect ? "button" : undefined}
        tabIndex={onSelect ? 0 : undefined}
        onClick={onSelect}
        onKeyDown={
          onSelect
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelect();
                }
              }
            : undefined
        }
        aria-pressed={onSelect ? isSelected : undefined}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.25,
          px: 2,
          pt: 2,
          pb: 1.5,
          cursor: onSelect ? "pointer" : "default",
          "&:focus-visible": { outline: `2px solid ${brand.pink}`, outlineOffset: -2, borderRadius: "16px" },
        }}
      >
        <PlatformIcon platform={platform} size={36} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.9375rem",
              color: "#111827",
              lineHeight: 1.3,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {hideTitleInHeader
              ? meta.label
              : list.length > 1
                ? title || meta.label
                : title
                  ? `${meta.label} · ${title}`
                  : meta.label}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#6B7280", mt: 0.25 }}>
            {adaptationMeta(selected)}
          </Typography>
        </Box>
        {status && (
          <Badge color={status.color} sx={{ flexShrink: 0 }}>
            {status.label}
          </Badge>
        )}
        {headerAction}
      </Box>
      )}

      {!embedded && list.length > 1 && (
        <Box sx={{ display: "flex", gap: 0.75, px: 2, pb: 1.5, flexWrap: "wrap" }}>
          {list.map((adaptation) => {
            const active = adaptation.id === selected?.id;
            const { label, color } = getPlatformMeta(adaptation.platform);
            return (
              <Box
                key={adaptation.id}
                component="button"
                type="button"
                onClick={() => handleSelect(adaptation)}
                title={label}
                aria-pressed={active}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.375,
                  borderRadius: "999px",
                  border: `1px solid ${active ? color : "#E5E7EB"}`,
                  backgroundColor: active ? `${color}12` : "#fff",
                  color: active ? color : "#4B5563",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <PlatformIcon platform={adaptation.platform} size={18} sx={{ backgroundColor: "transparent" }} />
                {label}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Publication format (Instagram, Facebook, TikTok): only the team changes it, before publishing. */}
      {!embedded && formatOptions.length > 1 && onChangeFormat && !isPublished && (
        <Box sx={{ px: 2, pb: 1.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
          {onChangeFormat && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
              <Typography component="span" sx={sectionLabelSx}>
                Formato
              </Typography>
              <FormatSelector
                options={formatOptions}
                value={format}
                onChange={requestFormatChange}
                disabled={Boolean(pendingFormat) || Boolean(activeUpload)}
                busyValue={pendingFormat}
                networkLabel={meta.label}
              />
            </Box>
          )}
          {onChangeFormat && currentFormatOption?.hint && (
            <Typography sx={{ fontSize: "0.75rem", color: "#6B7280", lineHeight: 1.45 }}>
              {currentFormatOption.hint}
            </Typography>
          )}
          {formatError && (
            <Typography role="alert" sx={{ fontSize: "0.75rem", color: "#B91C1C", fontWeight: 600 }}>
              {formatError}
            </Typography>
          )}
          {formatNotice && (
            <Typography role="status" sx={{ fontSize: "0.75rem", color: "#066CC0", fontWeight: 600 }}>
              {formatNotice}
            </Typography>
          )}
        </Box>
      )}

      {/* Body */}
      <Box sx={{ ...(embedded ? {} : { px: 2, pb: 2 }), display: "flex", flexDirection: "column", gap: embedded ? 2 : 1.5, flex: 1, minHeight: 0 }}>
        {embedded ? (
          // Text and media in one frame, split by a line: the media belongs to the post.
          <Box sx={{ border: "1px solid #E5E7EB", borderRadius: "12px", overflow: "hidden", backgroundColor: "#fff" }}>
            {textBlock}
            {mediaBlock && (
              <Box
                // Empty: the drop zone already takes the files. With files: the whole strip does.
                {...(canDropOnStrip
                  ? {
                      onDragOver: (event) => {
                        event.preventDefault();
                        setIsDraggingOver(true);
                      },
                      onDragLeave: (event) => {
                        if (!event.currentTarget.contains(event.relatedTarget)) setIsDraggingOver(false);
                      },
                      onDrop: (event) => {
                        event.preventDefault();
                        setIsDraggingOver(false);
                        handleFiles(event.dataTransfer.files);
                      },
                    }
                  : {})}
                sx={{
                  borderTop: "1px solid #E5E7EB",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  transition: "background-color 0.15s, box-shadow 0.15s",
                  ...(isDraggingOver ? { backgroundColor: brand.soft, boxShadow: `inset 0 0 0 2px ${brand.pink}` } : {}),
                }}
              >
                {mediaBlock}
              </Box>
            )}
          </Box>
        ) : (
          <>
            {textBlock}
            {mediaBlock}
          </>
        )}


        {!embedded && uploadNotice}

        {cropImage && (
          <ImageCropDialog
            image={cropImage}
            platform={platform}
            format={format}
            onClose={() => setCropImage(null)}
            onSave={(box) => onCropImage(selected, cropImage, box)}
          />
        )}
        <DocumentDialog
          open={Boolean(documentDialog)}
          mode={documentDialog || "add"}
          initialFile={documentDialog === "add" ? documentFile : null}
          initialTitle={documentDialog === "title" ? images.find(isDocumentMedia)?.title || "" : ""}
          onClose={() => {
            setDocumentDialog(null);
            setDocumentFile(null);
          }}
          onSubmit={submitDocument}
        />

        <UnifiedModal
          open={Boolean(formatConfirm)}
          onClose={() => !pendingFormat && setFormatConfirm(null)}
          title={`Cambiar a ${formatConfirm?.label || ""}`}
          maxWidth="xs"
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" sx={{ color: "#6B7280" }}>
              Esta publicación ya está programada en {meta.label}. Se actualizará en la red con el formato «
              {formatConfirm?.label}».
              {formatConfirm?.items.length
                ? ` ${formatConfirm.items.length === 1 ? "Se quitará 1 archivo" : `Se quitarán ${formatConfirm.items.length} archivos`} que este formato no admite.`
                : ""}
            </Typography>
            {formatError && (
              <AlertNote variant="danger" sx={{ p: 1.5 }}>
                {formatError}
              </AlertNote>
            )}
            {pendingFormat && <BusyNote message={`Actualizando el formato en ${meta.label}…`} />}
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button variant="outline" onClick={() => setFormatConfirm(null)} disabled={Boolean(pendingFormat)}>
                Cancelar
              </Button>
              <Button onClick={confirmScheduledFormat} disabled={Boolean(pendingFormat)}>
                {pendingFormat ? <ButtonSpinner /> : null}
                {pendingFormat ? "Actualizando..." : "Cambiar formato"}
              </Button>
            </Box>
          </Box>
        </UnifiedModal>

        {formatSwitch && (
          <AlertNote
            variant="warning"
            sx={{ p: 1.5 }}
            action={
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <Button variant="outline" size="sm" onClick={() => setFormatSwitch(null)}>
                  Cancelar
                </Button>
                <Button size="sm" onClick={confirmFormatSwitch}>
                  Cambiar y quitar
                </Button>
              </Box>
            }
          >
            {mediaWarnings(platform, formatSwitch.format, formatSwitch.items)[0]} Si cambias a «{formatSwitch.label}», se{" "}
            {formatSwitch.items.length === 1
              ? `quitará ${isVideoMedia(formatSwitch.items[0]) ? "el vídeo" : "la imagen"}`
              : `quitarán ${formatSwitch.items.length} archivos`}
            .
          </AlertNote>
        )}

        {videosForFormat && (
          <AlertNote
            variant="info"
            sx={{ p: 1.5 }}
            action={
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <Button variant="outline" size="sm" onClick={() => setVideosForFormat(null)}>
                  Cancelar
                </Button>
                <Button size="sm" onClick={() => switchFormatAndUpload(videosForFormat)}>
                  Cambiar y subir
                </Button>
              </Box>
            }
          >
            {formatLabel(platform, format)} no admite vídeo. ¿Cambiar el formato a «{videosForFormat.label}» y subir{" "}
            {videosForFormat.files.length === 1 ? `«${videosForFormat.files[0].name}»` : `${videosForFormat.files.length} vídeos`}?
            {images.length > 0 && " Las fotos actuales no se publicarán con ese formato."}
          </AlertNote>
        )}

        {rejected.length > 0 && (
          <AlertNote
            variant="danger"
            sx={{ p: 1.5 }}
            action={
              <IconButton size="small" aria-label="Cerrar aviso" onClick={() => setRejected([])} sx={{ p: 0.25 }}>
                <X size={15} />
              </IconButton>
            }
          >
            {rejected.length === 1 ? (
              <>
                No se ha añadido «{rejected[0].name}»: {rejected[0].reason}
              </>
            ) : (
              <>
                No se han añadido {rejected.length} archivos:
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                  {rejected.map((item, index) => (
                    <li key={`${item.name}-${index}`}>
                      «{item.name}»: {item.reason}
                    </li>
                  ))}
                </Box>
              </>
            )}
          </AlertNote>
        )}

        {faultyItems.length > 0 && (
          <AlertNote
            variant="danger"
            sx={{ p: 1.5 }}
            action={
              canReplaceFaulty ? (
                <Button size="sm" variant="outline" onClick={() => reuploadRef.current?.click()}>
                  <RefreshCw size={14} /> {isExpired ? "Volver a subir" : "Sustituir"}
                </Button>
              ) : null
            }
          >
            {errorInfo.message}
            <MediaInput
              inputRef={reuploadRef}
              accept={mediaAccept(platform, format)}
              multiple={faultyItems.length > 1}
              onFiles={handleReplaceFaulty}
            />
          </AlertNote>
        )}

        <MediaWarnings warnings={warnings} />

        {footerNote}
        {/* Actions stay at the bottom of cards stretched to the row's height. */}
        {actions && <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", mt: "auto" }}>{actions}</Box>}
      </Box>
    </Box>
  );
}
